/**
 * Source Intelligence Engine
 * 
 * Lightweight ranking, budget guidance, and scheduling assist logic.
 * No automation — purely advisory.
 */

/* ═══ Source Ranking ═══ */
export type SourceRank = "top_performer" | "average" | "underperforming";

export interface SourceRankResult {
  rank: SourceRank;
  label: string;
  badgeClass: string;
  textClass: string;
}

export function rankSource(
  netConversionPct: number,
  cpa: number,
  leadQualityPct: number,
): SourceRankResult {
  let score = 0;
  if (netConversionPct >= 40) score += 3;
  else if (netConversionPct >= 20) score += 2;
  else if (netConversionPct >= 10) score += 1;

  if (cpa > 0 && cpa <= 200) score += 3;
  else if (cpa > 0 && cpa <= 350) score += 2;
  else if (cpa > 0 && cpa <= 500) score += 1;

  if (leadQualityPct >= 75) score += 3;
  else if (leadQualityPct >= 60) score += 2;
  else if (leadQualityPct >= 40) score += 1;

  if (score >= 7) return {
    rank: "top_performer",
    label: "Top Performer",
    badgeClass: "bg-green-500/10 text-green-700 border-green-500/20",
    textClass: "text-green-600",
  };
  if (score >= 4) return {
    rank: "average",
    label: "Average",
    badgeClass: "bg-amber-500/10 text-amber-700 border-amber-500/20",
    textClass: "text-amber-600",
  };
  return {
    rank: "underperforming",
    label: "Underperforming",
    badgeClass: "bg-destructive/10 text-destructive border-destructive/20",
    textClass: "text-destructive",
  };
}

/* ═══ Budget Guidance ═══ */
export interface BudgetRecommendation {
  message: string;
  severity: "positive" | "neutral" | "warning";
  icon: "increase" | "monitor" | "reduce";
}

export function getBudgetRecommendation(
  rank: SourceRank,
  cpa: number,
  leadQualityPct: number,
  roi: number,
): BudgetRecommendation {
  if (rank === "top_performer" && roi > 200) {
    return { message: "Increase budget — strong performance and high ROI", severity: "positive", icon: "increase" };
  }
  if (rank === "top_performer") {
    return { message: "Increase budget — consistent conversion and quality", severity: "positive", icon: "increase" };
  }
  if (rank === "underperforming" && cpa > 400) {
    return { message: "Reduce spend — high CPA with low conversion", severity: "warning", icon: "reduce" };
  }
  if (rank === "underperforming") {
    return { message: "Reduce spend — low conversion rate", severity: "warning", icon: "reduce" };
  }
  if (leadQualityPct < 55) {
    return { message: "Monitor performance — lead quality trending low", severity: "neutral", icon: "monitor" };
  }
  return { message: "Monitor performance — within expected range", severity: "neutral", icon: "monitor" };
}

/* ═══ Sales Rep Locations ═══
 * Derived from the canonical roster — never hardcode rep identities here.
 * Routing-specific fields (availability, scheduleDensity, existingJobZips)
 * are sourced from the roster with safe defaults for any rep that omits them.
 */
import { SALES_REP_ROSTER } from "@/data/rosters/salesReps";

export interface RepProfile {
  name: string;
  homeBase: string;
  region: string;
  coverageZips: string[];     // zip prefixes this rep covers
  availability: string[];
  scheduleDensity: "light" | "moderate" | "heavy";
  existingJobZips: string[];  // zips of today's scheduled jobs (for clustering)
}

export const SALES_REPS: RepProfile[] = SALES_REP_ROSTER.map(r => ({
  name: r.name,
  homeBase: r.homeBase,
  region: r.region,
  coverageZips: r.coverageZips,
  availability: r.availability ?? ["AM", "PM"],
  scheduleDensity: r.scheduleDensity ?? "moderate",
  existingJobZips: r.existingJobZips ?? [],
}));

/* ═══ Routing Settings (defaults) ═══ */
export interface RoutingSettings {
  maxTravelDistanceMiles: number;
  preferTerritoryMatch: boolean;
  enableClusterOptimization: boolean;
  travelWarningThreshold: "moderate" | "strict";
  schedulingMode: "assistive" | "automated";
}

export const DEFAULT_ROUTING_SETTINGS: RoutingSettings = {
  maxTravelDistanceMiles: 30,
  preferTerritoryMatch: true,
  enableClusterOptimization: true,
  travelWarningThreshold: "moderate",
  schedulingMode: "assistive",
};

/* ═══ Geo Matching ═══ */
export type ProximityTag = "Nearby" | "Optimal Route" | "Same Territory" | "Longer Drive" | "Out of Range";

export interface GeoMatch {
  rep: RepProfile;
  proximityTag: ProximityTag;
  estimatedMiles: number;
  hasClustering: boolean;       // rep has nearby jobs same day
  isInTerritory: boolean;
  score: number;                // 0-100
}

/**
 * Lightweight zip-based distance estimation.
 * Same 3-digit prefix ≈ 0-15 mi, same first 2 digits ≈ 15-40 mi, else 40+ mi.
 */
function estimateDistance(zipA: string, zipB: string): number {
  if (zipA.slice(0, 3) === zipB.slice(0, 3)) return Math.floor(Math.random() * 12) + 3;   // 3-15 mi
  if (zipA.slice(0, 2) === zipB.slice(0, 2)) return Math.floor(Math.random() * 25) + 15;  // 15-40 mi
  return Math.floor(Math.random() * 40) + 40;                                               // 40-80 mi
}

export function matchLeadToReps(
  leadZip: string,
  settings: RoutingSettings = DEFAULT_ROUTING_SETTINGS,
): GeoMatch[] {
  const matches: GeoMatch[] = [];

  for (const rep of SALES_REPS) {
    const dist = estimateDistance(leadZip, rep.homeBase);
    const isInTerritory = rep.coverageZips.some(prefix => leadZip.startsWith(prefix));
    const hasClustering = rep.existingJobZips.some(z => z.slice(0, 3) === leadZip.slice(0, 3));

    // Determine proximity tag
    let proximityTag: ProximityTag;
    if (dist > settings.maxTravelDistanceMiles) {
      proximityTag = "Out of Range";
    } else if (hasClustering && dist <= 15) {
      proximityTag = "Optimal Route";
    } else if (dist <= 12) {
      proximityTag = "Nearby";
    } else if (isInTerritory) {
      proximityTag = "Same Territory";
    } else {
      proximityTag = "Longer Drive";
    }

    // Score calculation
    let score = 0;
    // Proximity (40 pts max)
    if (dist <= 10) score += 40;
    else if (dist <= 20) score += 30;
    else if (dist <= 30) score += 20;
    else score += 5;

    // Territory (20 pts)
    if (isInTerritory) score += 20;

    // Clustering (25 pts)
    if (hasClustering) score += 25;

    // Schedule density (15 pts)
    if (rep.scheduleDensity === "light") score += 15;
    else if (rep.scheduleDensity === "moderate") score += 8;

    score = Math.min(100, score);

    matches.push({ rep, proximityTag, estimatedMiles: dist, hasClustering, isInTerritory, score });
  }

  return matches
    .filter(m => m.proximityTag !== "Out of Range" || !settings.preferTerritoryMatch)
    .sort((a, b) => b.score - a.score);
}

/* ═══ Fit Score Labels ═══ */
export type FitLabel = "Best Option" | "Good Option" | "Lower Fit";

export interface FitScoreResult {
  fitScore: number;       // 0-100 unified
  fitLabel: FitLabel;
  fitBadgeClass: string;
  fitTextClass: string;
  microcopy: string;      // short human-readable reasoning
}

function buildMicrocopy(
  leadQuality: "High" | "Medium" | "Low",
  estimatedMiles: number,
  hasClustering: boolean,
  isInTerritory: boolean,
  scheduleDensity: "light" | "moderate" | "heavy",
): string {
  const parts: string[] = [];
  parts.push(leadQuality === "High" ? "High quality lead" : leadQuality === "Medium" ? "Moderate quality" : "Low quality lead");
  if (estimatedMiles <= 12) parts.push("Nearby");
  else if (estimatedMiles <= 20) parts.push("Moderate distance");
  else parts.push("Long drive");
  if (hasClustering) parts.push("Clustered");
  else if (scheduleDensity === "light") parts.push("Open schedule");
  else if (scheduleDensity === "heavy") parts.push("Low efficiency");
  return parts.join(" · ");
}

function deriveFitLabel(score: number): FitScoreResult {
  if (score >= 75) return {
    fitScore: score, fitLabel: "Best Option",
    fitBadgeClass: "bg-green-500/10 text-green-700 border-green-500/20",
    fitTextClass: "text-green-600",
    microcopy: "",
  };
  if (score >= 50) return {
    fitScore: score, fitLabel: "Good Option",
    fitBadgeClass: "bg-blue-500/10 text-blue-700 border-blue-500/20",
    fitTextClass: "text-blue-600",
    microcopy: "",
  };
  return {
    fitScore: score, fitLabel: "Lower Fit",
    fitBadgeClass: "bg-muted text-muted-foreground border-border",
    fitTextClass: "text-muted-foreground",
    microcopy: "",
  };
}

/* ═══ Rep Performance by Channel/Quality (seed data) ═══ */

interface RepChannelPerformance {
  repName: string;
  /** channel id → { leads assigned, net jobs, conversion % } */
  channelStats: Record<string, { leadsAssigned: number; netJobs: number; conversionPct: number }>;
  /** quality tier → { leads assigned, net jobs, conversion % } */
  qualityStats: Record<"High" | "Medium" | "Low", { leadsAssigned: number; netJobs: number; conversionPct: number }>;
}

/** Minimum leads in a channel/tier before performance boost is applied */
const PERFORMANCE_THRESHOLD = 10;

const REP_CHANNEL_PERFORMANCE: RepChannelPerformance[] = [
  {
    repName: "Wesley Woodfin",
    channelStats: { "meta-growth-engine": { leadsAssigned: 18, netJobs: 9, conversionPct: 52 }, "angi": { leadsAssigned: 14, netJobs: 5, conversionPct: 38 }, "thumbtack": { leadsAssigned: 10, netJobs: 4, conversionPct: 44 } },
    qualityStats: { High: { leadsAssigned: 28, netJobs: 16, conversionPct: 57 }, Medium: { leadsAssigned: 10, netJobs: 4, conversionPct: 42 }, Low: { leadsAssigned: 4, netJobs: 1, conversionPct: 28 } },
  },
  {
    repName: "Kyle Schall",
    channelStats: { "meta-growth-engine": { leadsAssigned: 15, netJobs: 7, conversionPct: 48 }, "angi": { leadsAssigned: 12, netJobs: 7, conversionPct: 55 }, "thumbtack": { leadsAssigned: 11, netJobs: 4, conversionPct: 40 } },
    qualityStats: { High: { leadsAssigned: 22, netJobs: 14, conversionPct: 64 }, Medium: { leadsAssigned: 12, netJobs: 5, conversionPct: 45 }, Low: { leadsAssigned: 4, netJobs: 1, conversionPct: 30 } },
  },
  {
    repName: "Lucas Schall",
    channelStats: { "meta-growth-engine": { leadsAssigned: 12, netJobs: 4, conversionPct: 30 }, "angi": { leadsAssigned: 8, netJobs: 2, conversionPct: 25 }, "thumbtack": { leadsAssigned: 10, netJobs: 4, conversionPct: 35 } },
    qualityStats: { High: { leadsAssigned: 12, netJobs: 5, conversionPct: 42 }, Medium: { leadsAssigned: 11, netJobs: 3, conversionPct: 27 }, Low: { leadsAssigned: 7, netJobs: 1, conversionPct: 18 } },
  },
  {
    repName: "Trent Hill",
    channelStats: { "meta-growth-engine": { leadsAssigned: 11, netJobs: 6, conversionPct: 55 }, "angi": { leadsAssigned: 8, netJobs: 3, conversionPct: 42 }, "thumbtack": { leadsAssigned: 6, netJobs: 3, conversionPct: 50 } },
    qualityStats: { High: { leadsAssigned: 18, netJobs: 10, conversionPct: 56 }, Medium: { leadsAssigned: 5, netJobs: 2, conversionPct: 44 }, Low: { leadsAssigned: 2, netJobs: 1, conversionPct: 32 } },
  },
];

export function getRepChannelPerformance(repName: string): RepChannelPerformance | undefined {
  return REP_CHANNEL_PERFORMANCE.find(r => r.repName === repName);
}

/**
 * Compute a small performance bonus (0-10 pts) ONLY when sufficient data exists.
 * Below PERFORMANCE_THRESHOLD leads → no boost, no signal (beta-safe).
 * Priority order preserved: distance → availability → performance (additive only).
 */
function computePerformanceBonus(
  repName: string,
  channelId: string | undefined,
  qualityTier: "High" | "Medium" | "Low",
): { bonus: number; signal?: string } {
  const perf = REP_CHANNEL_PERFORMANCE.find(r => r.repName === repName);
  if (!perf) return { bonus: 0 };

  let bonus = 0;
  const signals: string[] = [];

  // Channel-specific conversion boost (max 5 pts) — ONLY if ≥ threshold leads
  if (channelId && perf.channelStats[channelId]) {
    const stats = perf.channelStats[channelId];
    if (stats.leadsAssigned >= PERFORMANCE_THRESHOLD) {
      const channelName = channelId.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      if (stats.conversionPct >= 50) { bonus += 5; signals.push(`Strong performance with ${channelName} leads`); }
      else if (stats.conversionPct >= 40) { bonus += 3; }
    }
    // Below threshold → silent fallback to standard scoring
  }

  // Quality tier conversion boost (max 5 pts) — ONLY if ≥ threshold leads
  const qStats = perf.qualityStats[qualityTier];
  if (qStats.leadsAssigned >= PERFORMANCE_THRESHOLD) {
    if (qStats.conversionPct >= 55) { bonus += 5; signals.push("Top-performing rep for this lead type"); }
    else if (qStats.conversionPct >= 40) { bonus += 3; }
  }
  // Below threshold → silent fallback

  return { bonus: Math.min(10, bonus), signal: signals[0] };
}

/* ═══ Enhanced Booking Suggestions ═══ */

export interface BookingSuggestion {
  rep: string;
  timeWindow: string;
  proximityNote: string;
  proximityTag: ProximityTag;
  estimatedMiles: number;
  reason: string;
  score: number;
  fitScore: number;
  fitLabel: FitLabel;
  fitBadgeClass: string;
  microcopy: string;
  hasClustering: boolean;
  isInTerritory: boolean;
  warning?: string;
  /** Performance-aware signal for this rep + lead type */
  performanceSignal?: string;
  /** Performance bonus applied (0-10) */
  performanceBonus: number;
}

export function getBookingSuggestions(
  jobZip: string,
  settings: RoutingSettings = DEFAULT_ROUTING_SETTINGS,
  leadQualityPct: number = 70,
  channelId?: string,
): BookingSuggestion[] {
  const geoMatches = matchLeadToReps(jobZip, settings);
  const suggestions: BookingSuggestion[] = [];
  const quality: "High" | "Medium" | "Low" =
    leadQualityPct >= 75 ? "High" : leadQualityPct >= 50 ? "Medium" : "Low";

  // Lead quality contributes to fit score
  const qualityBonus = quality === "High" ? 15 : quality === "Medium" ? 8 : 0;

  for (const match of geoMatches) {
    // Performance bonus for this rep + lead type (max 10 pts — never overrides distance/availability)
    const { bonus: perfBonus, signal: perfSignal } = computePerformanceBonus(match.rep.name, channelId, quality);

    for (const slot of match.rep.availability) {
      const timeWindow = slot === "AM" ? "9:00 AM – 12:00 PM" : "1:00 PM – 4:00 PM";

      // Build reason
      let reason: string;
      if (match.hasClustering && match.estimatedMiles <= 15) {
        reason = "Optimal — nearby jobs already scheduled";
      } else if (match.rep.scheduleDensity === "light") {
        reason = "Light schedule — high availability";
      } else if (match.isInTerritory) {
        reason = "Within assigned territory";
      } else if (match.estimatedMiles <= 12) {
        reason = "Close to home base";
      } else {
        reason = "Available for this window";
      }

      // Travel warning
      let warning: string | undefined;
      if (match.estimatedMiles > 25) {
        warning = "Long travel — consider closer reps";
      } else if (match.proximityTag === "Longer Drive") {
        warning = "Outside primary territory";
      }

      // Unified fit score = geo score + lead quality bonus + performance bonus (capped at 100)
      const rawFit = Math.min(100, match.score + qualityBonus + perfBonus);
      const fit = deriveFitLabel(rawFit);
      const microcopy = buildMicrocopy(quality, match.estimatedMiles, match.hasClustering, match.isInTerritory, match.rep.scheduleDensity);

      suggestions.push({
        rep: match.rep.name,
        timeWindow,
        proximityNote: `~${match.estimatedMiles} mi`,
        proximityTag: match.proximityTag,
        estimatedMiles: match.estimatedMiles,
        reason,
        score: match.score,
        fitScore: rawFit,
        fitLabel: fit.fitLabel,
        fitBadgeClass: fit.fitBadgeClass,
        microcopy,
        hasClustering: match.hasClustering,
        isInTerritory: match.isInTerritory,
        warning,
        performanceSignal: perfSignal,
        performanceBonus: perfBonus,
      });
    }
  }

  return suggestions
    .sort((a, b) => b.fitScore - a.fitScore)
    .slice(0, 6);
}

/* ═══ Pre-Booking Insight ═══ */
export interface PreBookingInsight {
  leadSource: string;
  leadQuality: "High" | "Medium" | "Low";
  suggestedRep: string;
  suggestedTime: string;
  clusterNote?: string;
}

export function getPreBookingInsight(
  leadSource: string,
  leadZip: string,
  leadQualityPct: number,
): PreBookingInsight {
  const suggestions = getBookingSuggestions(leadZip);
  const top = suggestions[0];
  const quality: "High" | "Medium" | "Low" =
    leadQualityPct >= 75 ? "High" : leadQualityPct >= 50 ? "Medium" : "Low";

  return {
    leadSource,
    leadQuality: quality,
    suggestedRep: top?.rep || "Unassigned",
    suggestedTime: top?.timeWindow || "No slots available",
    clusterNote: top?.hasClustering ? "Rep has nearby jobs — optimal routing" : undefined,
  };
}

/* ═══ Booking Efficiency Warning ═══ */
export interface BookingWarning {
  type: "travel" | "territory" | "density";
  message: string;
  severity: "info" | "warning" | "critical";
}

export function getBookingWarnings(
  selectedRep: string,
  leadZip: string,
  settings: RoutingSettings = DEFAULT_ROUTING_SETTINGS,
): BookingWarning[] {
  const rep = SALES_REPS.find(r => r.name === selectedRep);
  if (!rep) return [];

  const warnings: BookingWarning[] = [];
  const dist = estimateDistance(leadZip, rep.homeBase);
  const isInTerritory = rep.coverageZips.some(prefix => leadZip.startsWith(prefix));

  if (dist > settings.maxTravelDistanceMiles) {
    warnings.push({
      type: "travel",
      message: `This booking creates ~${dist} mi travel — exceeds ${settings.maxTravelDistanceMiles} mi limit`,
      severity: "critical",
    });
  } else if (dist > settings.maxTravelDistanceMiles * 0.75) {
    warnings.push({
      type: "travel",
      message: `Long travel distance (~${dist} mi) — consider a closer rep`,
      severity: "warning",
    });
  }

  if (!isInTerritory && settings.preferTerritoryMatch) {
    warnings.push({
      type: "territory",
      message: `${selectedRep} is outside their preferred territory for this zip`,
      severity: "info",
    });
  }

  if (rep.scheduleDensity === "heavy") {
    warnings.push({
      type: "density",
      message: `${selectedRep} has a heavy schedule — may impact response quality`,
      severity: "warning",
    });
  }

  return warnings;
}
